// One entry per chapter. TopicPage renders from this, and the build script
// scripts/gen-pages.py writes a matching public/<level>/<slug>.html for each
// so that every topic has its own title, description and canonical URL.

const topics = [
// ───────────────────────────── S3 ─────────────────────────────
{
  level: "s3", ch: 1, slug: "factorization",
  en: "Factorization", zh: "因式分解",
  desc: "中三數學「因式分解」免費練習 PDF，中英雙版。提取公因式、平方差、完全平方、十字相乘、分組分解，附答案。",
  intro: "因式分解是把多項式寫成幾個因式相乘。它是中四一元二次方程、二次函數和有理函數的基礎 —— 這一章不熟，後面幾乎每一課都會卡住。",
  formulas: [
    ["平方差", "a² − b² = (a + b)(a − b)"],
    ["完全平方", "a² + 2ab + b² = (a + b)²　；　a² − 2ab + b² = (a − b)²"],
    ["十字相乘", "x² + (p + q)x + pq = (x + p)(x + q)"],
    ["分組分解", "ax + ay + bx + by = a(x + y) + b(x + y) = (a + b)(x + y)"],
  ],
  mistakes: [
    "只提了部分公因式就停手。先把公因式提到盡，再看括號內能不能繼續分解。",
    "把 a² + b² 當成可以分解。實數範圍內 a² + b² 不能分解，只有平方差 a² − b² 可以。",
    "十字相乘時符號搞錯。檢查方法很簡單：把答案乘回去對一次常數項和一次項。",
    "分解到一半就當完成。例如 x⁴ − 16 = (x² + 4)(x² − 4)，後面的 x² − 4 還可以再拆成 (x + 2)(x − 2)。",
  ],
},
{
  level: "s3", ch: 2, slug: "laws-of-indices",
  en: "Laws of Indices", zh: "指數定律",
  desc: "中三數學「指數定律」免費練習 PDF，中英雙版。正負指數、零指數、分數指數、科學記數法，附答案。",
  intro: "指數定律是中四指數函數和對數函數的入口。DSE 必修部分幾乎每年都會在化簡或方程裏用到。",
  formulas: [
    ["同底相乘除", "aᵐ × aⁿ = aᵐ⁺ⁿ　；　aᵐ ÷ aⁿ = aᵐ⁻ⁿ"],
    ["冪的冪", "(aᵐ)ⁿ = aᵐⁿ"],
    ["積與商的冪", "(ab)ⁿ = aⁿbⁿ　；　(a/b)ⁿ = aⁿ/bⁿ"],
    ["零指數與負指數", "a⁰ = 1（a ≠ 0）　；　a⁻ⁿ = 1/aⁿ"],
    ["分數指數", "a^(1/n) = ⁿ√a　；　a^(m/n) = ⁿ√(aᵐ)"],
  ],
  mistakes: [
    "把 (a + b)ⁿ 當成 aⁿ + bⁿ。指數定律只對乘除成立，對加減完全不成立。",
    "把 a⁰ 當成 0。只要 a ≠ 0，a⁰ 一定等於 1。",
    "把負指數當成負數。2⁻³ = 1/8，是正數，不是 −8。",
    "底數不同就直接加指數。2³ × 3³ 要先寫成 (2 × 3)³ = 6³，不是 6⁶。",
  ],
},
{
  level: "s3", ch: 3, slug: "percentages",
  en: "Percentages", zh: "百分數",
  desc: "中三數學「百分數」免費練習 PDF，中英雙版。百分變化、連續變化、折扣盈虧、單利複利，附答案。",
  intro: "百分數是 DSE 必修部分最常出現的應用題背景之一，而且常與數列、增長率一起考。",
  formulas: [
    ["百分變化", "百分變化 = (新值 − 原值) / 原值 × 100%"],
    ["連續變化", "最終值 = 原值 × (1 + r₁)(1 + r₂)…"],
    ["盈虧", "盈虧百分數 = (售價 − 成本) / 成本 × 100%"],
    ["複利", "A = P(1 + r/n)^(nt)，n 為每年複利次數"],
  ],
  mistakes: [
    "分母用錯。百分變化永遠以「原值」作分母，不是新值。",
    "以為先加 10% 再減 10% 會回到原數。實際是 ×1.1×0.9 = 0.99，少了 1%。",
    "連續兩次增加 10% 當成增加 20%。實際是 ×1.21，即 21%。",
    "盈虧百分數用售價作分母。定義是以成本作分母。",
  ],
},
{
  level: "s3", ch: 4, slug: "linear-inequalities",
  en: "Linear Inequalities in One Unknown", zh: "一元一次不等式",
  desc: "中三數學「一元一次不等式」免費練習 PDF，中英雙版。解不等式、數線表示、複合不等式、整數解，附答案。",
  intro: "解法與一元一次方程幾乎一樣，只有一條關鍵差異：乘或除以負數時，不等號方向要反轉。中五的二次不等式和線性規劃都建基於此。",
  formulas: [
    ["加減", "a > b　⟹　a + c > b + c"],
    ["乘除正數", "a > b 且 c > 0　⟹　ac > bc"],
    ["乘除負數（反號）", "a > b 且 c < 0　⟹　ac < bc"],
    ["數線表示", "> 與 < 用空心圈；≥ 與 ≤ 用實心圈"],
  ],
  mistakes: [
    "乘或除以負數時忘記反轉不等號 —— 這是最常見的失分點。",
    "數線端點實心空心畫錯，嚴格不等式要用空心圈。",
    "複合不等式只解了一半。「且」要取交集，「或」要取併集。",
    "題目問整數解時直接寫出範圍就交卷，沒有把整數逐個列出。",
  ],
},
{
  level: "s3", ch: 5, slug: "measures-of-central-tendency",
  en: "Measures of Central Tendency", zh: "集中趨勢的量度",
  desc: "中三數學「集中趨勢的量度」免費練習 PDF，中英雙版。平均數、中位數、眾數、分組數據、加權平均，附答案。",
  intro: "平均數、中位數、眾數三者的分別，以及甚麼時候用哪一個，是 DSE 統計題的固定考點。中五的離差量度會接著這一章。",
  formulas: [
    ["平均數", "x̄ = Σx / n"],
    ["加權平均", "x̄ = Σ(wx) / Σw"],
    ["分組數據平均數", "x̄ = Σ(fx) / Σf，x 取組中點"],
    ["中位數", "先由小到大排序；n 為奇數取中間項，n 為偶數取中間兩項的平均"],
  ],
  mistakes: [
    "計中位數前沒有先排序 —— 直接取中間那個數字就錯。",
    "偶數個數據時只取其中一個，沒有取中間兩項的平均。",
    "搞錯哪個量度受極端值影響。受影響的是平均數，中位數和眾數相對穩定。",
    "分組數據用組界而不是組中點去計平均數。",
  ],
},
{
  level: "s3", ch: 6, slug: "deductive-geometry",
  en: "Deductive Geometry", zh: "演繹幾何",
  desc: "中三數學「演繹幾何」免費練習 PDF，中英雙版。全等三角形、相似三角形、等腰三角形性質與證明，附答案。",
  intro: "這一章學的是怎樣寫證明。DSE 的幾何證明題每一步都要寫理由，格式錯就算答案對也會扣分。",
  formulas: [
    ["全等條件", "SSS、SAS、ASA、AAS、RHS"],
    ["相似條件", "AAA（三角相等）、三邊成比例、兩邊成比例且夾角相等"],
    ["等腰三角形", "兩腰相等 ⟺ 兩底角相等（等邊對等角）"],
    ["相似比", "邊長比 = k　⟹　面積比 = k²"],
  ],
  mistakes: [
    "用「SSA」作全等條件。SSA 不成立，只有直角三角形的 RHS 可以。",
    "寫對應頂點時次序亂了。△ABC ≅ △DEF 代表 A↔D、B↔E、C↔F，次序不能調換。",
    "每一步沒有寫理由。證明題的分數大部分在理由，不在結論。",
    "把相似當全等用，直接說對應邊相等；相似只保證成比例。",
  ],
},
{
  level: "s3", ch: 7, slug: "quadrilaterals",
  en: "Quadrilaterals", zh: "四邊形",
  desc: "中三數學「四邊形」免費練習 PDF，中英雙版。平行四邊形、長方形、菱形、正方形、梯形性質與判定，附答案。",
  intro: "這一章要分清楚「性質」和「判定」：性質是已知是平行四邊形才能用，判定是用來證明它是平行四邊形。考試最常考的就是這個方向。",
  formulas: [
    ["平行四邊形性質", "兩組對邊分別平行且相等；兩組對角相等；對角線互相平分"],
    ["平行四邊形判定", "兩組對邊分別相等、或兩組對角相等、或對角線互相平分、或一組對邊平行且相等"],
    ["菱形", "四邊相等；對角線互相垂直平分，並平分各內角"],
    ["中點定理", "連接三角形兩邊中點的線段平行第三邊，且等於第三邊的一半"],
  ],
  mistakes: [
    "把性質當判定用。已知是平行四邊形才可以說對角線互相平分，反過來要證明。",
    "以為對角線互相平分就是菱形。互相平分只能得出平行四邊形，要再加互相垂直才是菱形。",
    "梯形只有一組對邊平行，卻用了平行四邊形的性質。",
    "用中點定理時沒有確認兩點真的是中點。",
  ],
},
{
  level: "s3", ch: 8, slug: "3d-figures",
  en: "3D Figures", zh: "立體圖形",
  desc: "中三數學「立體圖形」免費練習 PDF，中英雙版。稜柱、角錐、圓柱、圓錐、球、歐拉公式、平面圖與投影，附答案。",
  intro: "這一章處理立體的頂點、棱、面與展開圖，是中三求積法和中五三維三角學的前置。",
  formulas: [
    ["歐拉公式", "V − E + F = 2（V 頂點、E 棱、F 面）"],
    ["角錐", "底面為 n 邊形時：頂點 n + 1、棱 2n、面 n + 1"],
    ["圓錐斜高", "l = √(r² + h²)"],
    ["正投影", "正視圖、側視圖、俯視圖三者互相對應"],
  ],
  mistakes: [
    "把斜高 l 和高 h 混淆。圓錐側面積用斜高，體積用高。",
    "數棱和面時漏掉底面。",
    "展開圖畫漏其中一面，或把圓錐展開圖的扇形半徑畫成 r 而不是 l。",
    "三視圖沒有對齊，長寬高對不上。",
  ],
},
{
  level: "s3", ch: 9, slug: "mensuration-area-and-volume",
  en: "Mensuration, Area and Volume", zh: "求積法，面積和體積",
  desc: "中三數學「求積法，面積和體積」免費練習 PDF，中英雙版。角錐、圓錐、球的面積體積、相似立體比例，附答案。",
  intro: "公式本身不難，真正的考點是相似立體的面積比與體積比 —— 這是 DSE 的高頻題型。",
  formulas: [
    ["角錐／圓錐體積", "V = ⅓ × 底面積 × 高　；　圓錐 V = ⅓πr²h"],
    ["圓錐表面積", "曲面面積 = πrl　；　總表面積 = πrl + πr²"],
    ["球", "V = ⁴⁄₃πr³　；　表面積 = 4πr²"],
    ["相似立體", "長度比 = k　⟹　面積比 = k²，體積比 = k³"],
  ],
  mistakes: [
    "相似立體的體積比用了 k² 而不是 k³ —— 這是這一章最常見的錯。",
    "角錐和圓錐的體積忘記乘 ⅓。",
    "單位換算出錯：1 m² = 10⁴ cm²，1 m³ = 10⁶ cm³，不是 100。",
    "求圓錐表面積時只計了曲面，漏了底圓。",
  ],
},
{
  level: "s3", ch: 10, slug: "applications-of-trigonometry",
  en: "Applications of Trigonometry", zh: "三角學的應用",
  desc: "中三數學「三角學的應用」免費練習 PDF，中英雙版。仰角俯角、方位角、羅盤方位角、實際測量問題，附答案。",
  intro: "這一章把 sin、cos、tan 放進實際情境：測高度、測距離、航行方向。中五的正弦餘弦公式會接著用同一套語言。",
  formulas: [
    ["基本比", "sin θ = 對邊/斜邊　；　cos θ = 鄰邊/斜邊　；　tan θ = 對邊/鄰邊"],
    ["仰角與俯角", "兩者都從水平線量起；仰角向上，俯角向下"],
    ["真方位角", "由正北起順時針量，寫成三位數，例如 075°"],
    ["羅盤方位角", "寫成 N θ° E 這種形式，由北或南量向東或西"],
  ],
  mistakes: [
    "方位角由錯誤的方向量起。真方位角一定由正北順時針量。",
    "仰角俯角畫反，或把角畫在鉛直線而不是水平線上。",
    "真方位角沒有補足三位數，把 75° 寫成 75° 而不是 075°。",
    "計算器停留在弧度模式，答案全錯。做題前先確認是 DEG。",
  ],
},
{
  level: "s3", ch: 11, slug: "coordinate-geometry-of-straight-line",
  en: "Coordinate Geometry of Straight Line", zh: "直線的坐標幾何",
  desc: "中三數學「直線的坐標幾何」免費練習 PDF，中英雙版。距離公式、中點、分點、斜率、平行垂直、直線方程，附答案。",
  intro: "這一章是中四直線方程和中五圓的方程的直接前置。斜率的平行垂直關係幾乎每年 DSE 都會考。",
  formulas: [
    ["兩點距離", "d = √[(x₂ − x₁)² + (y₂ − y₁)²]"],
    ["中點", "M = ((x₁ + x₂)/2, (y₁ + y₂)/2)"],
    ["斜率", "m = (y₂ − y₁) / (x₂ − x₁)"],
    ["平行與垂直", "平行：m₁ = m₂　；　垂直：m₁m₂ = −1"],
    ["點斜式", "y − y₁ = m(x − x₁)"],
  ],
  mistakes: [
    "斜率的分子分母倒轉，寫成 Δx/Δy。",
    "鉛直線（x = a）的斜率不存在，卻當成 0；水平線才是斜率 0。",
    "用垂直條件 m₁m₂ = −1 時，忘記其中一條線是鉛直線的特殊情況。",
    "內分點與外分點公式用錯比例次序。",
  ],
},
{
  level: "s3", ch: 12, slug: "probability",
  en: "Probability", zh: "概率",
  desc: "中三數學「概率」免費練習 PDF，中英雙版。古典概率、樹形圖、互斥事件、獨立事件、餘事件，附答案。",
  intro: "中三先建立古典概率和樹形圖，中五的排列組合和續概率會在這個基礎上加深。",
  formulas: [
    ["古典概率", "P(E) = 有利結果數目 / 所有可能結果數目"],
    ["餘事件", "P(E′) = 1 − P(E)"],
    ["互斥事件", "P(A ∪ B) = P(A) + P(B)"],
    ["獨立事件", "P(A ∩ B) = P(A) × P(B)"],
  ],
  mistakes: [
    "混淆互斥與獨立。互斥是不能同時發生，獨立是互不影響，兩者完全不同。",
    "沒有看清「有放回」還是「沒有放回」。沒有放回時第二次的分母要減一。",
    "樹形圖漏了分支，或忘記沿路徑相乘、跨路徑相加。",
    "答案寫成大於 1 或負數的概率，交卷前應該檢查一次。",
  ],
},
// ───────────────────────────── S4 ─────────────────────────────
{
  level: "s4", ch: 1, slug: "complex-number",
  en: "Complex Number", zh: "複數",
  desc: "中四數學「複數」免費練習 PDF，中英雙版。虛數單位、四則運算、共軛複數、複數相等，附答案。",
  intro: "當判別式小於零，一元二次方程在實數範圍內無解 —— 複數就是為了處理這種情況而引入的。這一章與一元二次方程的判別式直接相連。",
  formulas: [
    ["虛數單位", "i = √(−1)　，　i² = −1"],
    ["標準式", "z = a + bi，a 為實部、b 為虛部"],
    ["共軛複數", "若 z = a + bi，則 z̄ = a − bi　；　z·z̄ = a² + b²"],
    ["複數相等", "a + bi = c + di　⟺　a = c 且 b = d"],
    ["除法", "分子分母同乘分母的共軛，把分母化為實數"],
  ],
  mistakes: [
    "把 i² 當成 1 或 −i。i² = −1，而 i³ = −i、i⁴ = 1，四個一循環。",
    "把 √(−4) × √(−9) 當成 √36 = 6。正確是 2i × 3i = 6i² = −6。",
    "除法時忘記乘共軛，留下分母有 i。",
    "比較兩個複數的大小 —— 複數沒有大小次序，只能比較是否相等。",
  ],
},
{
  level: "s4", ch: 2, slug: "equation-of-straight-line",
  en: "Equation of Straight Line", zh: "直線方程",
  desc: "中四數學「直線方程」免費練習 PDF，中英雙版。點斜式、斜截式、兩點式、截距式、一般式、平行垂直，附答案。",
  intro: "這一章把中三的斜率知識整理成各種方程形式，是中五圓的方程和線性規劃的基礎。",
  formulas: [
    ["點斜式", "y − y₁ = m(x − x₁)"],
    ["斜截式", "y = mx + c"],
    ["兩點式", "(y − y₁)/(x − x₁) = (y₂ − y₁)/(x₂ − x₁)"],
    ["截距式", "x/a + y/b = 1"],
    ["一般式", "Ax + By + C = 0　，　斜率 = −A/B（B ≠ 0）"],
  ],
  mistakes: [
    "由一般式讀斜率時忘記負號。Ax + By + C = 0 的斜率是 −A/B，不是 A/B。",
    "鉛直線寫成 y = 某數。鉛直線是 x = a，水平線才是 y = b。",
    "用兩點式時把 x 和 y 的次序配錯，上下顛倒。",
    "題目要求「一般式」卻交了斜截式，格式分照扣。",
  ],
},
{
  level: "s4", ch: 3, slug: "quadratic-equations",
  en: "Quadratic Equations", zh: "一元二次方程",
  desc: "中四數學「一元二次方程」免費練習 PDF，中英雙版。因式分解法、公式法、配方法、判別式、根與係數關係，附答案。",
  intro: "DSE 必修部分最核心的一章之一。判別式和根與係數關係幾乎每年都考，而且常與二次函數、複數一起出題。",
  formulas: [
    ["二次公式", "x = [−b ± √(b² − 4ac)] / (2a)"],
    ["判別式", "Δ = b² − 4ac：Δ > 0 兩相異實根；Δ = 0 兩相等實根；Δ < 0 無實根"],
    ["根與係數", "α + β = −b/a　；　αβ = c/a"],
    ["配方法", "x² + bx = (x + b/2)² − (b/2)²"],
  ],
  mistakes: [
    "二次公式的分母寫成 2 而不是 2a。",
    "判別式忘記平方：寫成 b − 4ac。",
    "Δ = 0 時答「一個根」。正確說法是「兩個相等的實根」。",
    "用根與係數關係時忘記除以 a，直接寫 α + β = −b。",
  ],
},
{
  level: "s4", ch: 4, slug: "function",
  en: "Function", zh: "函數",
  desc: "中四數學「函數」免費練習 PDF，中英雙版。函數記號、定義域、值域、求值、複合函數，附答案。",
  intro: "函數記號 f(x) 一旦熟練，後面的二次函數、指數函數、對數函數都只是不同的 f。這一章要練的是讀懂記號。",
  formulas: [
    ["函數記號", "f(x) 表示把 x 代入後得到的值"],
    ["求值", "f(a) 即把函數中每一個 x 換成 a"],
    ["定義域", "所有容許的輸入值；分母不能為零，根號內不能為負"],
    ["複合函數", "fg(x) = f(g(x))，先做 g 再做 f"],
  ],
  mistakes: [
    "把 f(x + 1) 當成 f(x) + 1。前者是把 x + 1 整個代入。",
    "複合函數的次序做反。fg(x) 是先 g 後 f。",
    "求定義域時忘記分母不為零或根號內非負。",
    "把 f(x) 的 −1 次方與反函數 f⁻¹(x) 混淆。",
  ],
},
{
  level: "s4", ch: 5, slug: "quadratic-function",
  en: "Quadratic Function", zh: "二次函數",
  desc: "中四數學「二次函數」免費練習 PDF，中英雙版。拋物線、頂點、對稱軸、配方法、最大值最小值，附答案。",
  intro: "二次函數是 DSE 的常客，而且經常以應用題形式出現（求最大面積、最大利潤）。關鍵技巧是配方。",
  formulas: [
    ["一般式", "y = ax² + bx + c"],
    ["頂點式", "y = a(x − h)² + k，頂點為 (h, k)"],
    ["對稱軸", "x = −b/(2a)"],
    ["最值", "a > 0 時在頂點取最小值；a < 0 時在頂點取最大值"],
  ],
  mistakes: [
    "對稱軸忘記負號，寫成 x = b/(2a)。",
    "配方時沒有先把 a 提出來，例如 2x² + 8x 要先寫成 2(x² + 4x)。",
    "把頂點式 y = a(x − h)² + k 的 h 讀成 −h。y = (x + 3)² 的頂點是 (−3, 0)。",
    "應用題求出 x 之後就停手，沒有答回題目要求的最大值本身。",
  ],
},
{
  level: "s4", ch: 6, slug: "more-about-polynomials",
  en: "More about Polynomials", zh: "續多項式",
  desc: "中四數學「續多項式」免費練習 PDF，中英雙版。長除法、餘式定理、因式定理、多項式分解，附答案。",
  intro: "餘式定理和因式定理讓你不用做長除法就能求餘式或判斷因式，是 DSE 的高頻工具。",
  formulas: [
    ["除法算式", "f(x) = (除式) × (商式) + 餘式"],
    ["餘式定理", "f(x) 除以 (x − a) 的餘式為 f(a)"],
    ["因式定理", "(x − a) 是 f(x) 的因式　⟺　f(a) = 0"],
    ["一般線性除式", "f(x) 除以 (ax − b) 的餘式為 f(b/a)"],
  ],
  mistakes: [
    "用餘式定理時符號搞反。除以 (x + 2) 要代入 x = −2，不是 2。",
    "長除法時漏寫缺項。x³ + 1 要補成 x³ + 0x² + 0x + 1。",
    "除式是 (2x − 1) 時代入 x = 1，正確應該代入 x = ½。",
    "找到一個因式就停手，沒有把剩下的二次式繼續分解。",
  ],
},
{
  level: "s4", ch: 7, slug: "exponential-function",
  en: "Exponential Function", zh: "指數函數",
  desc: "中四數學「指數函數」免費練習 PDF，中英雙版。指數函數圖像、性質、指數方程、增長與衰變，附答案。",
  intro: "指數函數 y = aˣ 描述增長與衰變，是複利、人口、半衰期等應用題的數學模型，也是對數函數的鏡像。",
  formulas: [
    ["定義", "y = aˣ，其中 a > 0 且 a ≠ 1"],
    ["圖像特徵", "必過 (0, 1)；x 軸為漸近線；a > 1 遞增，0 < a < 1 遞減"],
    ["指數方程", "aˣ = aʸ　⟺　x = y"],
    ["增長／衰變", "A = A₀(1 + r)ᵗ　或　A = A₀(1 − r)ᵗ"],
  ],
  mistakes: [
    "以為指數函數會與 x 軸相交。x 軸只是漸近線，永遠不會接觸。",
    "解指數方程時底數不同就直接比較指數。要先化成同底。",
    "把 y = aˣ 與 y = xᵃ 混淆，前者是指數函數，後者是冪函數。",
    "衰變題用了 (1 + r) 而不是 (1 − r)。",
  ],
},
{
  level: "s4", ch: 8, slug: "logarithm-function",
  en: "Logarithm Function", zh: "對數函數",
  desc: "中四數學「對數函數」免費練習 PDF，中英雙版。對數定律、換底公式、對數方程、圖像與性質，附答案。",
  intro: "對數是指數的逆運算。只要記住 log_a b = c ⟺ aᶜ = b，大部分題目都能轉回指數來處理。",
  formulas: [
    ["定義", "log_a b = c　⟺　aᶜ = b（a > 0, a ≠ 1, b > 0）"],
    ["乘除", "log(MN) = log M + log N　；　log(M/N) = log M − log N"],
    ["冪", "log(Mⁿ) = n log M"],
    ["換底", "log_a b = (log b) / (log a)"],
    ["特殊值", "log_a 1 = 0　；　log_a a = 1"],
  ],
  mistakes: [
    "把 log(M + N) 當成 log M + log N。對數定律只對乘除成立。",
    "把 (log M)² 與 log(M²) 混淆，後者等於 2 log M。",
    "忘記檢查真數必須大於零，解出來的根要代回去驗證。",
    "log M / log N 當成 log(M/N)。前者是換底的形式。",
  ],
},
{
  level: "s4", ch: 9, slug: "rational-function",
  en: "Rational Function", zh: "有理函數",
  desc: "中四數學「有理函數」免費練習 PDF，中英雙版。有理式化簡、四則運算、繁分式、不容許值，附答案。",
  intro: "有理函數就是分式版本的多項式運算。技巧核心是先分解因式，再約簡。",
  formulas: [
    ["化簡", "先把分子分母分解因式，再約去公因式"],
    ["乘除", "(A/B) × (C/D) = AC/BD　；　(A/B) ÷ (C/D) = (A/B) × (D/C)"],
    ["加減", "先通分到最小公分母，再合併分子"],
    ["不容許值", "使分母為零的 x 值要排除"],
  ],
  mistakes: [
    "未分解因式就直接約簡，把 (x + 2)/(x + 4) 的 x 約掉。只有整個因式才能約。",
    "忘記寫出不容許值（例如 x ≠ 3）。",
    "加減時直接把分子分母分別相加。",
    "約簡後忘記原式的限制仍然成立。",
  ],
},
{
  level: "s4", ch: 10, slug: "basic-properties-of-circles-1",
  en: "Basic Properties of Circles 1", zh: "圓的基本性質 1",
  desc: "中四數學「圓的基本性質 1」免費練習 PDF，中英雙版。弦的性質、弦心距、垂直平分、等弦，附答案。",
  intro: "這一章集中處理弦與圓心的關係。所有結論都圍繞一句話：由圓心到弦的垂線平分該弦。",
  formulas: [
    ["垂直平分弦", "由圓心引向弦的垂線平分該弦，反之亦然"],
    ["弦心距與弦長", "等弦的弦心距相等；弦越長，弦心距越短"],
    ["弦長計算", "弦長 = 2√(r² − d²)，d 為弦心距"],
    ["三點定圓", "不共線的三點可以確定唯一一個圓"],
  ],
  mistakes: [
    "把弦心距 d 當成半徑 r 去計算。",
    "用弦長公式時忘記乘 2，只算了一半。",
    "假設所有通過圓心的線都垂直於弦，要有垂直或平分其中一個條件才能推另一個。",
    "證明題沒有寫出「線從圓心出發」這個前提。",
  ],
},
{
  level: "s4", ch: 11, slug: "basic-properties-of-circles-2",
  en: "Basic Properties of Circles 2", zh: "圓的基本性質 2",
  desc: "中四數學「圓的基本性質 2」免費練習 PDF，中英雙版。圓周角、圓心角、同弓形內的圓周角、圓內接四邊形，附答案。",
  intro: "圓周角定理一系列結論是 DSE 幾何證明題的主力。考試常把幾條定理串在一題裏，要能快速辨認圖形。",
  formulas: [
    ["圓心角與圓周角", "同弧所對的圓心角 = 2 × 圓周角"],
    ["同弓形內的圓周角", "同一弓形內的圓周角相等"],
    ["半圓上的圓周角", "直徑所對的圓周角為 90°"],
    ["圓內接四邊形", "對角互補（和為 180°）；外角等於內對角"],
  ],
  mistakes: [
    "圓心角與圓周角的倍數關係用反，寫成圓周角 = 2 × 圓心角。",
    "兩個角不是對著同一條弧卻當成相等。要先確認所對的弧相同。",
    "圓內接四邊形用了「對角相等」，正確是「對角互補」。",
    "沒有先證明四點共圓，就直接用圓內接四邊形的性質。",
  ],
},
{
  level: "s4", ch: 12, slug: "basic-trigonometry",
  en: "Basic Trigonometry", zh: "基本三角學",
  desc: "中四數學「基本三角學」免費練習 PDF，中英雙版。三角比推廣、單位圓、特殊角、三角恆等式、三角方程，附答案。",
  intro: "中三的三角比只處理銳角，這一章把它推廣到任意角，並引入恆等式與三角方程 —— 中五解三角形的基礎。",
  formulas: [
    ["ASTC 象限", "第一象限全正；第二象限只 sin 正；第三象限只 tan 正；第四象限只 cos 正"],
    ["基本恆等式", "sin²θ + cos²θ = 1　；　tan θ = sin θ / cos θ"],
    ["特殊角", "sin 30° = ½，cos 30° = √3/2，tan 45° = 1"],
    ["負角與補角", "sin(180° − θ) = sin θ　；　cos(180° − θ) = −cos θ"],
  ],
  mistakes: [
    "象限符號判斷錯。記 ASTC（All, Sin, Tan, Cos）由第一象限逆時針數。",
    "解三角方程只給出一個解，漏了同一範圍內的其他解。",
    "把 sin²θ 寫成 sin(θ²)。sin²θ 是 (sin θ)²。",
    "計算器在弧度模式。做題前確認是 DEG。",
  ],
},
// ───────────────────────────── S5 ─────────────────────────────
{
  level: "s5", ch: 1, slug: "more-about-equations",
  en: "More about Equations", zh: "續方程",
  desc: "中五數學「續方程」免費練習 PDF，中英雙版。高次方程、無理方程、指數對數方程、二次聯立方程，附答案。",
  intro: "這一章教你把看似陌生的方程化成熟悉的一元二次方程 —— 用代換、平方、或代入消元。",
  formulas: [
    ["代換法", "x⁴ − 13x² + 36 = 0 設 u = x²，化為 u² − 13u + 36 = 0"],
    ["無理方程", "先隔離根號再平方，必須驗根"],
    ["二次聯立", "把直線方程代入曲線方程，化為一元二次方程"],
    ["交點數目", "由判別式判斷：Δ > 0 兩交點，Δ = 0 相切，Δ < 0 不相交"],
  ],
  mistakes: [
    "無理方程平方後沒有驗根。平方會製造增根，必須代回原式檢查。",
    "用代換法求出 u 之後忘記換回 x。",
    "x⁴ = 16 只寫 x = 2，漏了 x = −2。",
    "聯立時用減法消元卻把其中一條方程的每一項都忘記變號。",
  ],
},
{
  level: "s5", ch: 2, slug: "quadratic-inequalities",
  en: "Quadratic Inequalities in One Unknown", zh: "一元二次不等式",
  desc: "中五數學「一元二次不等式」免費練習 PDF，中英雙版。圖像法、表列法、解集表示，附答案。",
  intro: "解二次不等式最穩陣的方法是畫拋物線草圖：先求根，再看圖像在 x 軸上方還是下方。",
  formulas: [
    ["步驟", "移項使右邊為 0　→　分解或用公式求根　→　畫草圖　→　讀出解集"],
    ["a > 0 時", "ax² + bx + c < 0 的解在兩根之間；> 0 的解在兩根之外"],
    ["無實根時", "Δ < 0 且 a > 0，則對所有 x 恆有 ax² + bx + c > 0"],
    ["解集表示", "可用不等式、數線或區間表示"],
  ],
  mistakes: [
    "直接把不等式當方程解，只寫出兩個根就當答案。",
    "a < 0 時忘記拋物線開口向下，解集方向剛好相反。",
    "「兩根之間」與「兩根之外」記反。畫草圖就不會錯。",
    "合併解集時把「或」寫成「且」，得出空集。",
  ],
},
{
  level: "s5", ch: 3, slug: "functions-and-graphs",
  en: "Functions and Graphs", zh: "函數與圖像",
  desc: "中五數學「函數與圖像」免費練習 PDF，中英雙版。圖像平移、伸縮、反射等變換，附答案。",
  intro: "這一章處理圖像變換：由 y = f(x) 推出 y = f(x − h) + k、y = af(x)、y = f(−x) 等圖像的關係。",
  formulas: [
    ["水平平移", "y = f(x − h)：向右移 h 個單位"],
    ["鉛直平移", "y = f(x) + k：向上移 k 個單位"],
    ["鉛直伸縮", "y = af(x)：鉛直方向伸長 a 倍"],
    ["反射", "y = −f(x) 關於 x 軸；y = f(−x) 關於 y 軸"],
  ],
  mistakes: [
    "水平平移方向搞反。y = f(x − 3) 是向右移 3，不是向左。",
    "把 y = f(2x) 當成水平伸長 2 倍，實際是壓縮至原來的 ½。",
    "複合變換時次序做錯，先伸縮後平移與先平移後伸縮結果不同。",
    "只平移了圖像卻忘記頂點、截距等關鍵點也要跟著移。",
  ],
},
{
  level: "s5", ch: 4, slug: "permutation-and-combination",
  en: "Permutation and Combination", zh: "排列與組合",
  desc: "中五數學「排列與組合」免費練習 PDF，中英雙版。加法乘法原理、排列 nPr、組合 nCr、限制條件問題，附答案。",
  intro: "分清楚「次序重要」還是「次序不重要」，是這一章唯一但最關鍵的判斷。次序重要用排列，不重要用組合。",
  formulas: [
    ["乘法原理", "分步完成：各步方法數相乘"],
    ["加法原理", "分類完成：各類方法數相加"],
    ["排列", "ⁿPᵣ = n! / (n − r)!（次序重要）"],
    ["組合", "ⁿCᵣ = n! / [r!(n − r)!]（次序不重要）"],
    ["關係", "ⁿPᵣ = ⁿCᵣ × r!　；　ⁿCᵣ = ⁿCₙ₋ᵣ"],
  ],
  mistakes: [
    "選隊員（不分次序）卻用了排列，答案大了 r! 倍。",
    "「至少一個」直接逐類相加而漏項。用餘事件（全部減去一個都沒有）更穩。",
    "相鄰問題忘記把捆綁的一組內部也要排列。",
    "分步與分類混淆：分步相乘，分類相加。",
  ],
},
{
  level: "s5", ch: 5, slug: "more-about-probability",
  en: "More about Probability", zh: "續概率",
  desc: "中五數學「續概率」免費練習 PDF，中英雙版。條件概率、相依事件、排列組合在概率的應用，附答案。",
  intro: "中三的概率加上中五的排列組合，就是這一章。重點是條件概率和有放回／沒有放回的分別。",
  formulas: [
    ["加法法則", "P(A ∪ B) = P(A) + P(B) − P(A ∩ B)"],
    ["條件概率", "P(A | B) = P(A ∩ B) / P(B)"],
    ["乘法法則", "P(A ∩ B) = P(B) × P(A | B)"],
    ["獨立事件", "A、B 獨立　⟺　P(A ∩ B) = P(A)P(B)"],
  ],
  mistakes: [
    "用 P(A ∪ B) = P(A) + P(B) 卻沒有減去重疊部分，除非兩者互斥。",
    "條件概率的分母用錯。P(A | B) 的分母是 P(B)，不是 P(A)。",
    "沒有放回時第二次抽取的分母忘記減一。",
    "把互斥當獨立。互斥事件（且概率不為零）一定不獨立。",
  ],
},
{
  level: "s5", ch: 6, slug: "variation",
  en: "Variation", zh: "變分",
  desc: "中五數學「變分」免費練習 PDF，中英雙版。正變、反變、聯變、部分變、百分變化問題，附答案。",
  intro: "變分題的解法有固定三步：寫出關係式並引入常數 k、用已知數據求 k、再代入求答案。",
  formulas: [
    ["正變", "y ∝ x　⟹　y = kx"],
    ["反變", "y ∝ 1/x　⟹　y = k/x"],
    ["聯變", "z ∝ xy　⟹　z = kxy"],
    ["部分變", "y = k₁ + k₂x（一部分為常數，一部分隨 x 正變）"],
    ["複合", "z ∝ x²/y　⟹　z = kx²/y"],
  ],
  mistakes: [
    "忘記引入比例常數 k，直接寫 y = x。",
    "部分變只設一個常數。部分變要兩個常數，需要兩組數據才能解出。",
    "百分變化題直接把百分數加減。應該用比值：x 增加 20% 即乘 1.2。",
    "「隨 x² 正變」寫成 (kx)²。正確是 kx²。",
  ],
},
{
  level: "s5", ch: 7, slug: "equation-of-circle",
  en: "Equation of Circle", zh: "圓的方程",
  desc: "中五數學「圓的方程」免費練習 PDF，中英雙版。標準式、一般式、圓心半徑、點與圓的關係、切線與交點，附答案。",
  intro: "這一章把中三的坐標幾何和中四的圓性質接起來。核心是在標準式與一般式之間自由轉換。",
  formulas: [
    ["標準式", "(x − h)² + (y − k)² = r²，圓心 (h, k)、半徑 r"],
    ["一般式", "x² + y² + Dx + Ey + F = 0"],
    ["由一般式求圓心半徑", "圓心 (−D/2, −E/2)　；　r = √(D²/4 + E²/4 − F)"],
    ["點與圓", "把點代入左邊：小於 r² 在圓內、等於在圓上、大於在圓外"],
    ["直線與圓", "代入後用判別式：Δ > 0 兩交點、Δ = 0 相切、Δ < 0 不相交"],
  ],
  mistakes: [
    "由標準式讀圓心時忘記變號。(x + 3)² + (y − 2)² = 9 的圓心是 (−3, 2)。",
    "由一般式求半徑時漏了減 F，或忘記開方。",
    "配方時只在括號內加了數，沒有在等式另一邊補回。",
    "求切線時用了圓心到點的斜率，忘記切線與半徑垂直，斜率要取負倒數。",
  ],
},
{
  level: "s5", ch: 8, slug: "locus",
  en: "Locus", zh: "軌跡",
  desc: "中五數學「軌跡」免費練習 PDF，中英雙版。基本軌跡的描述、由條件求軌跡方程（等距、定距、PA = kPB、直角、拋物線），附答案。",
  intro: "軌跡就是「滿足某個條件的所有點」。先用文字描述它是甚麼圖形，再設 P(x, y)，把條件寫成方程 —— 這一章把中三、中四的坐標幾何（距離公式、斜率、直線方程）和中五的圓的方程連起來。",
  formulas: [
    ["與定點距離固定", "以該點為圓心的圓：(x − h)² + (y − k)² = r²，其中 (h, k) 為定點，r 為固定距離"],
    ["與兩定點距離相等", "線段 AB 的垂直平分線；由 PA² = PB² 化簡得直線方程"],
    ["與定直線距離固定", "一對與該直線平行、分別位於它兩旁並與它相距該固定距離的直線"],
    ["與兩平行線距離相等", "位於兩線正中間的平行線"],
    ["與兩相交直線距離相等", "兩線所成各角的一對角平分線"],
    ["與線段距離固定", "封閉曲線：兩條與線段平行、等長並相距該距離的線段，加上兩個以線段端點為圓心的半圓"],
    ["與定點及定直線距離相等", "拋物線；由「P 與定點的距離 = P 與定直線的距離」平方後化簡得方程"],
  ],
  mistakes: [
    "由 PA = PB 求方程時沒有先平方。先寫成 PA² = PB²，再展開化簡。",
    "「與直線的距離是 3」只寫了一條直線，漏了另一邊的平行線。",
    "∠APB = 90° 的軌跡是以 AB 為直徑的圓，但不包括 A 和 B 兩點。",
    "把 PA = 2PB 平方後應得 PA² = 4PB²，不是 PA² = 2PB²。",
  ],
},
{
  level: "s5", ch: 9, slug: "solving-triangles",
  en: "Solving Triangles", zh: "解三角形",
  desc: "中五數學「解三角形」免費練習 PDF，中英雙版。正弦公式、餘弦公式、三角形面積 ½ab sin C、希羅公式、兩解情況、方位及仰角應用題，附答案。",
  intro: "已知三角形的部分邊和角，求其餘的邊、角和面積。關鍵是選對公式：已知兩角及一邊（先用內角和求第三角），或已知一對「邊和它的對角」，就用正弦公式；已知兩邊及其夾角求第三邊，或已知三邊求角，就用餘弦公式。",
  formulas: [
    ["正弦公式", "a / sin A = b / sin B = c / sin C（a、b、c 分別是 ∠A、∠B、∠C 的對邊）"],
    ["餘弦公式（求邊）", "a² = b² + c² − 2bc cos A"],
    ["餘弦公式（求角）", "cos A = (b² + c² − a²) / (2bc)"],
    ["三角形面積", "面積 = ½ab sin C"],
    ["希羅公式", "面積 = √[s(s − a)(s − b)(s − c)]，其中 s = (a + b + c) / 2"],
  ],
  mistakes: [
    "用正弦公式求角時漏了鈍角的解：sin C = 0.9 時，C 可以是 64.2° 或 116°，要逐一檢查三角形內角和是否仍小於 180°。",
    "計算機設定在弧度（RAD）模式，所有答案都錯。先確認是度（DEG）。",
    "餘弦公式漏了 2bc，或忽略了鈍角的餘弦是負數。",
    "中途答案先四捨五入再計算下一步，令最後答案出現誤差。中途應保留更多位數，或使用計算機的記憶功能。",
  ],
},
{
  level: "s5", ch: 10, slug: "applications-of-trigonometry-3d",
  en: "Applications of Trigonometry (3D)", zh: "三角學的應用",
  desc: "中五數學「三角學的應用」免費練習 PDF，中英雙版。立體圖形中直線與平面的交角、兩平面的交角、最大斜率線、立體問題中的仰角和方位角，附答案。",
  intro: "這一章把三角學搬到立體空間。每題的關鍵是找出合適的三角形（多數是直角三角形）：先找垂足和投影，再用三角比；在非直角三角形中則用正弦公式或餘弦公式。",
  formulas: [
    ["直線與平面的交角", "直線與它在平面上的投影之間的角"],
    ["兩平面的交角", "在交線上取一點，分別在兩個平面內作垂直於交線的直線，兩線之間的角"],
    ["最大斜率線", "斜面上垂直於斜面與水平面交線的直線；它與水平面的交角就是斜面的傾角"],
    ["長方體的對角線", "√(a² + b² + c²)"],
    ["斜面上的路", "sin（路的傾角）= sin（斜面傾角）× cos（路與最大斜率線的夾角）"],
  ],
  mistakes: [
    "把直線與平面的交角，當成直線與平面上任意一條直線的夾角。必須用直線在平面上的投影。",
    "求兩平面的交角時，所用的兩條直線沒有都垂直於交線。",
    "立體圖中看似直角的角未必是直角，要由題目條件判斷。",
    "混淆方位和仰角：方位在水平面上量度，仰角在鉛垂面上量度。",
  ],
},
{
  level: "s5", ch: 11, slug: "measures-of-dispersion",
  en: "Measures of Dispersion", zh: "離差的量度",
  desc: "中五數學「離差的量度」免費練習 PDF，中英雙版。分佈域、四分位數間距、框線圖、幹葉圖、標準差（未分組數據、次數分佈表、分組數據），附答案。",
  intro: "平均數告訴你數據的中心，離差告訴你數據有多分散。這一章學三種量度：分佈域、四分位數間距和標準差，以及怎樣用框線圖比較兩組數據。",
  formulas: [
    ["分佈域", "最大值 − 最小值"],
    ["四分位數間距", "Q₃ − Q₁（Q₁、Q₃ 分別是下半部和上半部數據的中位數；數據個數是單數時，中位數不計入任何一半）"],
    ["標準差", "σ = √[Σ(x − x̄)² / n]"],
    ["次數分佈表", "σ = √[Σf(x − x̄)² / Σf] = √[Σfx² / Σf − x̄²]"],
    ["分組數據", "用組中點代表每組，所得的平均數和標準差都是估計值"],
  ],
  mistakes: [
    "求四分位數前沒有先把數據由小至大排列。",
    "數據個數是單數時，求 Q₁ 和 Q₃ 把中位數也算進下半部或上半部。",
    "分組數據用了組界而不是組中點計算。",
    "計算機用了樣本標準差 sx（有些型號標示為 xσn−1），應該用 σx（xσn）。",
  ],
},
{
  level: "s5", ch: 12, slug: "more-about-dispersion",
  en: "More about Dispersion", zh: "續離差",
  desc: "中五數學「續離差」免費練習 PDF，中英雙版。標準分、正態分佈的 68%–95%–99.7% 規則、數據變換對平均數和各種離差量度的影響，附答案。",
  intro: "這一章利用標準差作比較：標準分把不同科目的成績放在同一把尺上；正態分佈讓你由平均數和標準差估計百分率；最後探討增減數據，以及把每個數據加上或乘以常數後，各種離差量度怎樣改變。",
  formulas: [
    ["標準分", "z = (x − x̄) / σ"],
    ["正態分佈", "約 68%、95%、99.7% 的數據分別在平均數 ±1σ、±2σ、±3σ 之內"],
    ["每個數據加 k", "平均數加 k；標準差、分佈域、四分位數間距不變"],
    ["每個數據乘以 k（k > 0）", "平均數、標準差、分佈域、四分位數間距都乘以 k"],
    ["每個數據變為 ax + b（a > 0）", "平均數變為 a x̄ + b；標準差、分佈域、四分位數間距都乘以 a；標準分不變"],
    ["加入／刪去一個等於平均數的數據", "平均數不變；標準差變小／變大（原標準差不為 0 時）"],
  ],
  mistakes: [
    "計算標準分時除以平均數，而不是除以標準差。",
    "以為每個數據加 k 後標準差也加 k。加減常數不會改變離差。",
    "只求單邊時忘記把百分率減半：由平均數至平均數以上 1σ 之間只有約 34%，不是 68%。",
    "比較兩科表現只看原始分數，沒有考慮各科的平均數和標準差。",
  ],
},
// ───────────────────────────── S6 ─────────────────────────────
{
  level: "s6", ch: 1, slug: "arithmetic-sequences",
  en: "Arithmetic Sequences", zh: "等差數列",
  desc: "中六數學「等差數列」免費練習 PDF，中英雙版。數列的通項 T(n)、公差、由已知條件求首項和公差、判斷某數是否數列的項、等差中項、等差級數求和 S(n)、由總和求項數、倍數之和及座位、儲蓄、堆疊圓木應用題，附答案。",
  intro: "等差數列是由第二項起，每一項都等於前一項加上同一個數（公差）的數列，而等差級數就是把它的項加起來。這一章先由通項 T(n) 求指定的項和項數，再用 S(n) 的公式求總和；應用題的關鍵是看出題目中「每次多（或少）同一個數量」的規律，並分清題目求某一項還是總和。",
  formulas: [
    ["通項", "T(n) = a + (n − 1)d，其中 a 是首項，d 是公差"],
    ["公差", "d = T(n + 1) − T(n)，對所有 n 都是同一個常數"],
    ["項數", "n = (l − a) / d + 1，其中 l 是末項"],
    ["等差中項", "若 x、y、z 是等差數列的連續三項，則 y = (x + z) / 2"],
    ["等差級數的和", "S(n) = n/2 [2a + (n − 1)d]"],
    ["已知首項及末項", "S(n) = n/2 (a + l)"],
    ["性質", "等差數列的每一項加上同一個常數 k，公差不變；每一項乘以 k，公差變為 kd"],
  ],
  mistakes: [
    "把通項寫成 a + nd。第 n 項只比首項多了 (n − 1) 個公差，應是 T(n) = a + (n − 1)d。",
    "數項數時漏了「+ 1」：8, 13, 18, …, 233 共有 (233 − 8) / 5 + 1 = 46 項，不是 45 項。",
    "公差是「後項減前項」：40, 34, 28, … 的公差是 −6，不是 6。",
    "由 S(n) 求 n 時沒有捨去負根；求「S(n) 大於某數的最小 n」時，應取大於方程正根的最小整數，並以 S(n − 1) 和 S(n) 驗算。",
  ],
},
{
  level: "s6", ch: 2, slug: "geometric-sequences",
  en: "Geometric Sequences", zh: "等比數列",
  desc: "中六數學「等比數列」免費練習 PDF，中英雙版。判斷等比數列、公比、通項 T(n)、由已知條件求首項和公比（包括負公比及兩個可能值）、等比中項、等比級數求和 S(n)、無限等比級數的和 S(∞)、循環小數化分數、用對數求最小的 n，以及折舊、人口增長、彈球和儲蓄計劃應用題，附答案。",
  intro: "等比數列是每一項都等於前一項乘以同一個數（公比）的數列，而等比級數就是把它的項加起來。這一章先由通項 T(n) 求指定的項、首項和公比，再用 S(n) 的公式求總和；當 −1 < r < 1 時，無限多項的和會趨向一個定值 S(∞)，可用來把循環小數化為分數。折舊、人口增長、彈球和儲蓄計劃都是等比數列的應用，求「最少要多少年」時要用到中四學過的對數。",
  formulas: [
    ["公比", "r = T(n + 1) ÷ T(n)，對所有 n 都是同一個常數"],
    ["通項", "T(n) = arⁿ⁻¹，其中 a 是首項，r 是公比"],
    ["等比中項", "若 a、G、b 組成等比數列，則 G² = ab，即 G = ±√(ab)"],
    ["等比級數的和", "S(n) = a(1 − rⁿ) / (1 − r) = a(rⁿ − 1) / (r − 1)，其中 r ≠ 1"],
    ["無限等比級數的和", "−1 < r < 1 時，S(∞) = a / (1 − r)；r ≤ −1 或 r ≥ 1 時，和不存在"],
    ["增長及折舊", "每年增長 p%：n 年後的值 = 原值 × (1 + p%)ⁿ；每年折舊 p%：n 年後的值 = 原值 × (1 − p%)ⁿ"],
    ["用對數求 n", "解 bⁿ > c（b > 0，c > 0）：兩邊取對數得 n log b > log c；若 0 < b < 1，log b 是負數，兩邊除以 log b 時不等號要反轉"],
  ],
  mistakes: [
    "把通項寫成 arⁿ。第 n 項只比首項多乘了 (n − 1) 次公比，應是 T(n) = arⁿ⁻¹。",
    "由 r² = 9 只寫 r = 3，漏了 r = −3。兩個情況都要考慮，再用題目的其他條件（例如「首 3 項之和是負數」）取捨。",
    "未檢查 −1 < r < 1 就用 S(∞) = a / (1 − r)：2 + 3 + 9/2 + … 的公比是 3/2，無限等比級數的和不存在。",
    "解 0.85ⁿ < 0.25 時，兩邊取對數得 n log 0.85 < log 0.25；log 0.85 是負數，除以它時要把不等號反轉，得 n > 8.53，所以 n 的最小值是 9。",
  ],
},
{
  level: "s6", ch: 3, slug: "linear-programming",
  en: "Linear Inequalities and Linear Programming", zh: "二元一次不等式及線性規劃",
  desc: "中六數學「二元一次不等式及線性規劃」免費練習 PDF，中英雙版。判斷點是否滿足不等式、實線與虛線、由陰影區域寫出聯立不等式、可行解區域的頂點、目標函數的最大值和最小值、整數解，以及租車、飼料和生產應用題，附答案。",
  intro: "二元一次不等式表示坐標平面上一條直線其中一側的區域；把幾個不等式的區域重疊，就得到可行解區域。線性規劃就是在可行解區域內求目標函數 P = ax + by + c 的最大值或最小值；若可行解區域有界（並包括邊界），只須比較各頂點的值。這一章亦包括應用題：由文字列出約束條件，以及 x 和 y 必須是整數的情況。",
  formulas: [
    ["點是否滿足不等式", "把點的坐標代入不等式；若不等式成立，該點就位於不等式所表示的區域內"],
    ["實線與虛線", "≤ 或 ≥：邊界線畫成實線（包括邊界）；< 或 >：邊界線畫成虛線（不包括邊界）"],
    ["區域在直線的哪一側", "取一個不在邊界線上的測試點（通常用原點 (0, 0)）代入不等式：若成立，區域與測試點位於同一側；否則位於另一側"],
    ["可行解區域", "同時滿足所有約束條件的點所組成的區域；它的頂點是兩條邊界線的交點，可解聯立方程求得"],
    ["線性規劃（頂點法）", "若可行解區域是有界的（並包括邊界），目標函數 P = ax + by + c 的最大值和最小值在頂點出現，把各頂點代入 P 比較即可；若兩個相鄰頂點同時取得最大（或最小）值，連接它們的邊上每一點都取得該值。若區域無界（例如向右上方伸延），最大值或最小值可能不存在，但若存在，仍在頂點出現"],
    ["整數解", "若 x 和 y 必須是整數，而最佳頂點的坐標不是整數，就要檢查可行解區域內該頂點附近坐標為整數的點"],
  ],
  mistakes: [
    "把 < 或 > 的邊界線畫成實線，或把 ≤ 或 ≥ 的邊界線畫成虛線。",
    "以為「≤」的區域一定在直線下方。例如 x − 2y ≤ 4 所表示的區域在直線 x − 2y = 4 的上方；應代入測試點判斷。",
    "列應用題的約束條件時漏了 x ≥ 0、y ≥ 0，或忘記 x 和 y 必須是整數。",
    "最佳頂點的坐標不是整數時，直接把坐標四捨五入。所得的點可能不在可行解區域內，也未必是最佳的整數解，應比較頂點附近坐標為整數的點。",
  ],
},
{
  level: "s6", ch: 4, slug: "uses-and-abuses-of-statistics",
  en: "Uses and Abuses of Statistics", zh: "統計的應用及誤用",
  desc: "中六數學「統計的應用及誤用」免費練習 PDF，中英雙版。統計研究的步驟、總體與樣本、簡單隨機抽樣、系統抽樣、分層抽樣及樣本大小、有偏差的樣本、問卷設計、誤導的棒形圖、象形圖和折線圖、平均數、離差及百分率的誤用、評論新聞報道，附答案。",
  intro: "統計數字可以幫助我們了解事實，也可以被用來誤導別人。這一章先學怎樣做一項統計研究：確定目的、用合適的抽樣方法選出有代表性的樣本、設計公正清楚的問卷；再學怎樣識破誤導的統計圖表，以及平均數、離差和百分率的誤用。評論一項說法時，要具體指出錯在哪裏，最好再用數字說明實際情況。",
  formulas: [
    ["統計研究的步驟", "確定問題 → 收集數據 → 組織數據 → 表達數據 → 分析數據 → 闡釋結果"],
    ["系統抽樣", "每 k 個中抽取 1 個，k = 總體大小 ÷ 樣本大小；在 1 至 k 中隨機選一個數 r，再選出第 r、r + k、r + 2k、… 個"],
    ["分層抽樣", "從某一層選出的數目 = 樣本大小 × 該層的數目 ÷ 總體大小，即按各層的大小按比例分配，不是平均分給各層"],
    ["百分變化", "百分變化 = (新值 − 原值) ÷ 原值 × 100%，分母必須是原值"],
    ["連續百分變化", "先跌 r% 再升 r%，結果是原值 × (1 − r%)(1 + r%)，比原值低；例如先跌 40% 再升 40%，即 0.6 × 1.4 = 0.84，下跌 16%"],
    ["合併平均數", "x̄ = (n₁x̄₁ + n₂x̄₂ + n₃x̄₃) ÷ (n₁ + n₂ + n₃)，不可把各組平均數直接相加再除以組數"],
    ["立體象形圖", "邊長放大 k 倍，體積變為 k³ 倍；要以體積表示數量變為 m 倍，邊長只應放大 ∛m 倍"],
  ],
  mistakes: [
    "設計問卷時用了誘導性問題、含糊字眼（如「經常」）、一題問兩件事，或互相重疊的選項（如 $20 同屬兩個選項）。",
    "計算百分變化時用了新值做分母：售價由 $500 減至 $400 是減價 20%，不是 25%。",
    "只比較棒的高度，沒有留意縱軸不是由 0 開始，誤以為差距很大。",
    "數據有極端值時仍用平均數代表一般水平，其實中位數較合適。",
  ],
},
];

export default topics;
